import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useCheckUnderwriting, usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { BooleanRadioOptions, FormFlowPage } from "src/components";
import { createStyles } from "src/styles";

import { enums } from "@fraction/shared";

export interface ResidencyStatusCAProps {
  value?: enums.ResidencyStatus;
  onChange: (value: enums.ResidencyStatus) => void;
  title?: string;
  applicantId?: string;

  focusRef?: Ref<HTMLDivElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const styles = createStyles({
  options: {
    marginBottom: 30,
  },
});

const ResidencyStatusCA = ({
  value,
  onChange,
  onNext,
  onBack,
  style,
  focusRef,
  title,
  applicantId,
  ...props
}: ResidencyStatusCAProps) => {
  const validate = useCallback(() => !!value, [value]);

  const handleChange = useCallback(
    (isResidentOrPR: boolean) => {
      onChange(
        isResidentOrPR
          ? enums.ResidencyStatus.CA_CITIZEN_OR_PERMANENT_RESIDENT
          : enums.ResidencyStatus.CA_NONE_OF_ABOVE
      );
    },
    [onChange]
  );

  const runUnderwriting = useCheckUnderwriting({
    failMessage: "Unfortunately, Fraction only lends to citizens or permanent residents.",
    getSpecificUnderwritingResult: (result) => {
      if (!applicantId) {
        return;
      }
      return result?.applicantProperties?.[applicantId]?.[
        enums.UnderwritingRuleName.APPLICANT_RESIDENCY_CHECK
      ];
    },
    onNext,
    onChange: handleChange,
  });

  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({
    onNext: runUnderwriting.mutateAsync,
    validate,
  });

  return (
    <FormFlowPage
      {...props}
      ref={focusRef}
      title={title || "Are you a Canadian citizen or permanent resident?"}
      description="Fraction can lend to either Canadian citizens or permanent residents."
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={runUnderwriting.nextDisabled || nextDisabled}
      nextLoading={runUnderwriting.isPending || nextLoading}
    >
      <BooleanRadioOptions
        style={styles.options}
        onChange={runUnderwriting.onChange}
        value={
          value === undefined ? undefined : value === enums.ResidencyStatus.CA_CITIZEN_OR_PERMANENT_RESIDENT
        }
      />
    </FormFlowPage>
  );
};

export default memo(ResidencyStatusCA);
